import React, { useEffect, useState } from 'react';
import {
    Box, Flex, Heading, Text, Button, Spinner, Alert, AlertIcon,
    AlertDescription, useColorModeValue, useToast, Badge, HStack,
    VStack, Divider, Tag, TagLabel, TagLeftIcon, Icon,
    Modal, ModalOverlay, ModalContent, ModalHeader,
    ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
    Tooltip, Skeleton, SkeletonText, keyframes,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    MdCheckCircle, MdCancel, MdLink, MdLinkOff, MdStorefront,
    MdSecurity, MdRefresh, MdArrowBack, MdOpenInNew,
} from 'react-icons/md';
import { useStateContext } from '../../../context/ContextProvider';
import { useYouCanStatus } from '../../../hooks/useYouCanStatus';
import axiosClient, { linkAPI } from '../../../axios-client';

// Pulse animation for the connect button
const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(66, 153, 225, 0.5); }
    70% { box-shadow: 0 0 0 14px rgba(66, 153, 225, 0); }
    100% { box-shadow: 0 0 0 0 rgba(66, 153, 225, 0); }
`;

export default function YouCanConnect() {
    const { t } = useTranslation();
    const { user, currentBusiness } = useStateContext();
    const toast = useToast();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [disconnecting, setDisconnecting] = useState(false);
    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

    // ── Theme colors ─────────────────────────────────────────────────
    const cardBg = useColorModeValue('white', 'gray.700');
    const borderC = useColorModeValue('gray.200', 'gray.600');
    const subText = useColorModeValue('gray.500', 'gray.400');
    const pageBg = useColorModeValue('gray.50', 'gray.800');
    const connectedBg = useColorModeValue('green.50', 'green.900');
    const disconnectedBg = useColorModeValue('red.50', 'red.900');
    const youcanGradient = 'linear(to-r, #5469d4, #7b68ee, #a855f7)';
    const youcanColor = useColorModeValue('#5469d4', '#a78bfa');

    // ── YouCan status hook ───────────────────────────────────────────
    const {
        connected, storeSlug, scopes, connectedAt, loading, error, refetch,
    } = useYouCanStatus(user?.id, currentBusiness?.id);

    // ── Handle ?youcan_connected=1 or ?youcan_error=... from callback ──
    useEffect(() => {
        if (searchParams.get('youcan_connected') === '1') {
            toast({
                title: t('YouCan connecté avec succès !', 'YouCan connected successfully!'),
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            // Clean up the URL
            searchParams.delete('youcan_connected');
            setSearchParams(searchParams, { replace: true });
            refetch();
        }

        const youcanError = searchParams.get('youcan_error');
        if (youcanError) {
            const errorMessages = {
                access_denied: t("Le vendeur a refusé l'autorisation.", "The seller denied authorization."),
                invalid_state: t("Paramètre d'état invalide ou expiré.", "Invalid or expired state parameter."),
                no_code: t("Aucun code d'autorisation reçu.", "No authorization code received."),
                token_exchange_failed: t("Échec de l'échange de token.", "Token exchange failed."),
            };
            toast({
                title: t('Erreur de connexion YouCan', 'YouCan Connection Error'),
                description: errorMessages[youcanError] || youcanError,
                status: 'error',
                duration: 7000,
                isClosable: true,
            });
            searchParams.delete('youcan_error');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams]);

    // ── Connect handler ──────────────────────────────────────────────
    const handleConnect = () => {
        if (!user?.id || !currentBusiness?.id) {
            toast({
                title: t('Erreur', 'Error'),
                description: t(
                    'Veuillez sélectionner un business avant de connecter YouCan.',
                    'Please select a business before connecting YouCan.'
                ),
                status: 'warning',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        // Full-page redirect to the Django install endpoint
        const installUrl = `${linkAPI}/api/youcan/install?user_id=${user.id}&business=${currentBusiness.id}`;
        window.location.href = installUrl;
    };

    // ── Disconnect handler ───────────────────────────────────────────
    const handleDisconnect = async () => {
        setDisconnecting(true);
        try {
            await axiosClient.post('/api/youcan/disconnect/', {
                user_id: user.id,
                business: currentBusiness.id,
            });
            toast({
                title: t('YouCan déconnecté', 'YouCan Disconnected'),
                description: t(
                    'La connexion YouCan a été supprimée avec succès.',
                    'The YouCan connection has been removed successfully.'
                ),
                status: 'info',
                duration: 5000,
                isClosable: true,
            });
            refetch();
        } catch (err) {
            toast({
                title: t('Erreur', 'Error'),
                description: err.response?.data?.error || t(
                    'Échec de la déconnexion de YouCan.',
                    'Failed to disconnect YouCan.'
                ),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setDisconnecting(false);
            onConfirmClose();
        }
    };

    // ── Scopes display ───────────────────────────────────────────────
    const scopesList = scopes ? scopes.split(' ').filter(Boolean) : [];

    return (
        <Box p={{ base: 4, md: 6 }} bg={pageBg} minH="60vh">
            {/* Header */}
            <Flex align="center" mb={6} gap={3}>
                <Button
                    variant="ghost"
                    leftIcon={<MdArrowBack />}
                    onClick={() => navigate('/mes-apis')}
                    size="sm"
                >
                    {t('Retour', 'Back')}
                </Button>
            </Flex>

            {/* Main Card */}
            <Box
                maxW="700px"
                mx="auto"
                bg={cardBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={borderC}
                overflow="hidden"
                shadow="lg"
            >
                {/* Card Header with YouCan Branding */}
                <Box
                    bgGradient={youcanGradient}
                    px={6}
                    py={8}
                    textAlign="center"
                >
                    <Flex justify="center" mb={4}>
                        <Box
                            bg="white"
                            borderRadius="xl"
                            p={3}
                            shadow="lg"
                        >
                            <Icon as={MdStorefront} boxSize={10} color="#5469d4" />
                        </Box>
                    </Flex>
                    <Heading size="lg" color="white" mb={2}>
                        YouCan
                    </Heading>
                    <Text color="whiteAlpha.800" fontSize="sm">
                        {t(
                            'Connectez votre boutique YouCan pour synchroniser les commandes et les produits.',
                            'Connect your YouCan store to sync orders and products.'
                        )}
                    </Text>
                </Box>

                {/* Card Body */}
                <Box px={6} py={6}>
                    {loading ? (
                        <VStack spacing={4}>
                            <Skeleton height="20px" width="200px" />
                            <SkeletonText noOfLines={3} width="100%" />
                        </VStack>
                    ) : connected ? (
                        /* ── Connected State ── */
                        <VStack spacing={5} align="stretch">
                            <Flex
                                align="center"
                                bg={connectedBg}
                                borderRadius="lg"
                                px={4}
                                py={3}
                            >
                                <Icon as={MdCheckCircle} color="green.500" boxSize={5} mr={3} />
                                <Box>
                                    <Text fontWeight="bold" fontSize="sm">
                                        {t('Connecté', 'Connected')}
                                    </Text>
                                    <Text fontSize="xs" color={subText}>
                                        {storeSlug && (
                                            <>
                                                {t('Boutique', 'Store')}: <strong>{storeSlug}</strong>
                                            </>
                                        )}
                                    </Text>
                                </Box>
                                <Badge
                                    ml="auto"
                                    colorScheme="green"
                                    variant="subtle"
                                    fontSize="xs"
                                    px={2}
                                    py={1}
                                    borderRadius="md"
                                >
                                    {t('Actif', 'Active')}
                                </Badge>
                            </Flex>

                            {/* Connection Details */}
                            <VStack spacing={3} align="stretch" fontSize="sm">
                                {connectedAt && (
                                    <Flex justify="space-between">
                                        <Text color={subText}>{t('Connecté le', 'Connected on')}</Text>
                                        <Text fontWeight="medium">
                                            {new Date(connectedAt).toLocaleDateString()}
                                        </Text>
                                    </Flex>
                                )}

                                {scopesList.length > 0 && (
                                    <Box>
                                        <Text color={subText} mb={2} fontSize="xs">
                                            <Icon as={MdSecurity} mr={1} />
                                            {t('Permissions accordées', 'Granted permissions')}
                                        </Text>
                                        <HStack wrap="wrap" spacing={2}>
                                            {scopesList.map((scope) => (
                                                <Tag
                                                    key={scope}
                                                    size="sm"
                                                    colorScheme="purple"
                                                    variant="subtle"
                                                    borderRadius="md"
                                                >
                                                    <TagLeftIcon as={MdCheckCircle} boxSize={3} />
                                                    <TagLabel>{scope}</TagLabel>
                                                </Tag>
                                            ))}
                                        </HStack>
                                    </Box>
                                )}
                            </VStack>

                            <Divider />

                            {/* Actions */}
                            <Flex gap={3} justify="flex-end">
                                <Tooltip label={t('Rafraîchir le statut', 'Refresh status')}>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        leftIcon={<MdRefresh />}
                                        onClick={refetch}
                                    >
                                        {t('Rafraîchir', 'Refresh')}
                                    </Button>
                                </Tooltip>
                                <Button
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    leftIcon={<MdLinkOff />}
                                    onClick={onConfirmOpen}
                                    isLoading={disconnecting}
                                >
                                    {t('Déconnecter', 'Disconnect')}
                                </Button>
                            </Flex>
                        </VStack>
                    ) : (
                        /* ── Disconnected State ── */
                        <VStack spacing={5} align="stretch">
                            <Alert
                                status="info"
                                variant="left-accent"
                                borderRadius="md"
                            >
                                <AlertIcon />
                                <AlertDescription fontSize="sm">
                                    {t(
                                        "Votre boutique YouCan n'est pas encore connectée. Cliquez sur le bouton ci-dessous pour autoriser l'accès.",
                                        "Your YouCan store is not connected yet. Click the button below to authorize access."
                                    )}
                                </AlertDescription>
                            </Alert>

                            {error && (
                                <Alert status="error" variant="left-accent" borderRadius="md">
                                    <AlertIcon />
                                    <AlertDescription fontSize="sm">{error}</AlertDescription>
                                </Alert>
                            )}

                            <VStack spacing={3}>
                                <Text fontSize="sm" color={subText} textAlign="center">
                                    {t(
                                        'En connectant YouCan, vous autorisez ECOLINKE à :',
                                        'By connecting YouCan, you authorize ECOLINKE to:'
                                    )}
                                </Text>
                                <VStack align="start" spacing={1} fontSize="sm">
                                    <HStack>
                                        <Icon as={MdCheckCircle} color="green.400" />
                                        <Text>{t('Lire vos commandes', 'Read your orders')}</Text>
                                    </HStack>
                                    <HStack>
                                        <Icon as={MdCheckCircle} color="green.400" />
                                        <Text>{t('Lire vos produits', 'Read your products')}</Text>
                                    </HStack>
                                    <HStack>
                                        <Icon as={MdCheckCircle} color="green.400" />
                                        <Text>{t('Lire vos clients', 'Read your customers')}</Text>
                                    </HStack>
                                </VStack>
                            </VStack>

                            <Button
                                size="lg"
                                bgGradient={youcanGradient}
                                color="white"
                                _hover={{
                                    bgGradient: 'linear(to-r, #4558c9, #6a57dd, #9745e6)',
                                    transform: 'translateY(-1px)',
                                    shadow: 'lg',
                                }}
                                _active={{ transform: 'translateY(0)' }}
                                leftIcon={<MdLink />}
                                onClick={handleConnect}
                                animation={`${pulse} 2s infinite`}
                                w="100%"
                                borderRadius="lg"
                                py={6}
                            >
                                {t('Connecter YouCan', 'Connect YouCan')}
                            </Button>

                            <Text fontSize="xs" color={subText} textAlign="center">
                                {t(
                                    "Vous serez redirigé vers YouCan pour autoriser l'application.",
                                    'You will be redirected to YouCan to authorize the application.'
                                )}
                            </Text>
                        </VStack>
                    )}
                </Box>
            </Box>

            {/* Disconnect Confirmation Modal */}
            <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
                <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
                <ModalContent borderRadius="xl">
                    <ModalHeader>
                        <Flex align="center" gap={2}>
                            <Icon as={MdCancel} color="red.500" />
                            {t('Confirmer la déconnexion', 'Confirm Disconnect')}
                        </Flex>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm">
                            {t(
                                'Êtes-vous sûr de vouloir déconnecter votre boutique YouCan ? Les webhooks seront désabonnés et les nouvelles commandes ne seront plus synchronisées.',
                                'Are you sure you want to disconnect your YouCan store? Webhooks will be unsubscribed and new orders will no longer be synchronized.'
                            )}
                        </Text>
                        {storeSlug && (
                            <Tag mt={3} colorScheme="red" variant="subtle">
                                <TagLabel>{storeSlug}</TagLabel>
                            </Tag>
                        )}
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={onConfirmClose} size="sm">
                            {t('Annuler', 'Cancel')}
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={handleDisconnect}
                            isLoading={disconnecting}
                            size="sm"
                            leftIcon={<MdLinkOff />}
                        >
                            {t('Déconnecter', 'Disconnect')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
